import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { EnvelopeSimple, Pulse } from "@phosphor-icons/react";
import api from "@/lib/api";
import { publicOrigin } from "@/lib/landerDomain";
import { BTN_BLACK, BTN_WHITE, INPUT, errText } from "@/components/pageSettings/AdminModal";
import { PixelCheckRow, STATUS } from "./PixelCheckRow";
import { runLiveTest } from "./pixelLiveTest";

function MainSummary({ d }) {
  return (
    <div className="space-y-2" data-testid="pixel-check-main">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="label-mono">Main pixels</span>
        {d.main.length ? d.main.map((p) => (
          <span key={p.kind} className={`border border-black px-1.5 py-0.5 text-[10px] ${p.ok ? "bg-black text-white" : "bg-red-600 text-white"}`}>{p.kind} {p.id}</span>
        )) : <span className="text-xs text-red-700 font-bold">None set up</span>}
        <span className="label-mono ml-2 text-zinc-500">{d.extra} extra company pixel{d.extra === 1 ? "" : "s"}</span>
      </div>
      {[...d.main_problems, ...(d.kill_switch ? [{ level: "warn", text: "Media buyer tracking codes are blocked by the kill switch" }] : [])].map((p) => (
        <div key={p.text} className={`border-2 p-2 text-xs ${p.level === "fail" ? "border-red-600 bg-red-50 text-red-800" : "border-amber-500 bg-amber-50"}`} data-testid="pixel-check-main-problem">
          {p.text}
        </div>
      ))}
    </div>
  );
}

// Marketing Pixels: which pixels load on every live page and its thank-you page, and a live test of each.
export function PixelCheck() {
  const [d, setD] = useState(null);
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [live, setLive] = useState({});
  const [running, setRunning] = useState(null);
  const origins = Array.from(new Set([publicOrigin(), window.location.origin]));
  const [origin, setOrigin] = useState(origins[0]);
  const [params, setParams] = useSearchParams();
  const only = params.get("page");
  const load = () => api.get("/admin/pixel-check").then((r) => setD(r.data)).catch((e) => toast.error(errText(e, "Couldn't run the pixel check")));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (d && only) document.querySelector('[data-testid="pixel-check"]')?.scrollIntoView({ block: "start" });
  }, [d, only]);
  if (!d) return <div className="brut-card p-4 mb-6 label-mono" data-testid="pixel-check-loading">Checking pixels…</div>;
  const base = only ? d.rows.filter((r) => r.key === only) : d.rows;
  const rows = onlyProblems ? base.filter((r) => r.status !== "ok") : base;
  const run = async () => {
    setLive({});
    setRunning({ done: 0, total: rows.length * 2 });
    const results = await runLiveTest(rows, origin, (r) => {
      setLive((m) => ({ ...m, [r.key]: { ...(m[r.key] || {}), [r.surface]: r } }));
      setRunning((s) => ({ ...s, done: s.done + 1 }));
    });
    setRunning(null);
    await api.post("/admin/pixel-check/live", { results }).catch(() => null);
    const bad = results.filter((r) => !r.ok).length;
    if (bad) toast.error(`${bad} live check${bad === 1 ? "" : "s"} failed`);
    else toast.success("Every page loaded its pixels");
    load();
  };
  const emailNow = () => api.post("/admin/pixel-check/email-now")
    .then((r) => toast.success(r.data.sent ? `Emailed ${r.data.sent} admin(s)` : "Nothing new to email"))
    .catch((e) => toast.error(errText(e, "Couldn't send the email")));
  return (
    <section className="brut-card bg-white p-4 mb-6 space-y-4" data-testid="pixel-check">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="font-chivo font-black uppercase tracking-tighter text-lg">Pixel check</h2>
        {!only && Object.entries(d.counts).map(([k, n]) => (
          <span key={k} className={`label-mono ${STATUS[k][1]}`} data-testid={`pixel-check-count-${k}`}>{n} {STATUS[k][2].toLowerCase()}</span>
        ))}
        <span className="text-[10px] text-zinc-500 ml-auto" data-testid="pixel-check-live-at">
          {d.live_at ? `Last live test ${new Date(d.live_at).toLocaleString()}` : "No live test yet"}
        </span>
      </div>
      <p className="text-xs text-zinc-600 max-w-3xl">
        Every live page and its thank-you page. Main pixels always load; buyer, extra company and approved media buyer codes load on top.
        The live test opens each page and checks the pixel scripts start, without sending any page view or lead.
      </p>
      <MainSummary d={d} />
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-full sm:w-auto sm:min-w-[260px]">
          <select className={INPUT} value={origin} onChange={(e) => setOrigin(e.target.value)} disabled={Boolean(running)} data-testid="pixel-check-origin">
            {origins.map((o) => <option key={o} value={o}>Test on {o.replace(/^https?:\/\//, "")}</option>)}
          </select>
        </div>
        <button type="button" className={BTN_BLACK} onClick={run} disabled={Boolean(running) || !rows.length} data-testid="pixel-check-run-live">
          <Pulse size={11} weight="bold" /> {running ? `Testing ${running.done} of ${running.total}…` : `Run live test (${rows.length} pages)`}
        </button>
        <label className="flex items-center gap-1.5 text-xs">
          <input type="checkbox" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} data-testid="pixel-check-only-problems" /> Only pages with problems
        </label>
        <button type="button" className={`${BTN_WHITE} ml-auto`} onClick={emailNow} data-testid="pixel-check-email-now">
          <EnvelopeSimple size={11} weight="bold" /> Email problems now
        </button>
      </div>
      {only && (
        <div className="flex items-center gap-2 border-2 border-black bg-zinc-50 px-2 py-1 text-xs" data-testid="pixel-check-single">
          Showing one page{base.length ? "" : " (it isn't live, so it isn't checked)"}
          <button type="button" className="ml-auto font-bold underline" onClick={() => setParams({})} data-testid="pixel-check-show-all">Show all pages</button>
        </div>
      )}
      <ul className="border-2 border-black divide-y divide-zinc-200 max-h-[640px] overflow-y-auto" data-testid="pixel-check-rows">
        {rows.length ? rows.map((r) => <PixelCheckRow key={r.key} r={r} origin={origin} live={live[r.key]} liveFresh={d.live_fresh} />)
          : <li className="p-3 text-xs text-zinc-600" data-testid="pixel-check-empty">No pages with problems.</li>}
      </ul>
      <p className="text-[11px] text-zinc-500">A short email goes to admins each morning when something breaks (not again for the same problems).</p>
    </section>
  );
}
